
import Component from 'vue-class-component';
import Vue from 'vue';
import { namespace } from 'vuex-class';
import { LoanDetails } from '@/loan/types/loan-details';
import { Loan } from '@/loan/types/loan';
import { LoanUpdateLoanRequest } from '@/loan/types/loan-update-loan-request';
import { LoanTransactionWithDetails } from '@/loan/types/loan-transaction-with-details';
import LoanSummaryRecentTransactions from '@/loan/pages/LoanSummary/components/LoanSummaryRecentTransactions.vue';
import LoanSummaryProjection from '@/loan/pages/LoanSummary/components/LoanSummaryProjection.vue';
import LoanSummaryFIIcon from '@/loan/pages/LoanSummary/components/LoanSummaryFIIcon.vue';
import LoanSummaryMetaData from '@/loan/pages/LoanSummary/components/LoanSummaryMetaData.vue';
import { SessionUser } from '@/core/types/session-user';
import { LoanFrequency } from '@/loan/enums/loan-frequency';
import { LoanStatus } from '@/loan/enums/loan-status';
import { LoanProjection } from '@/loan/types/loan-projection';
import { LoanAPIService } from '@/loan/services/api';
import { CoreAPIService } from '@/core/services/api';
import { LoanProjectionChartContext } from '@/loan/types/loan-projection-chart-context';
import LoanESignModal from '@/loan/components/LoanESignModal.vue';
import { HelpCenterFIContactInfo } from '@/help-center/types/help-center-fi-contact-info';
import { LoanESignModalState } from '@/loan/types/loan-esign-modal-state';
import { LoanHelper } from '@/loan/helper';
import SummaryCard from '@/core/components/SummaryCard.vue';
import StatusBadge from '@/core/components/StatusBadge.vue';
import { LoanPaymentStatus } from '@/loan/enums/loan-payment-status';
import LoanFullyPaidWidget from '@/loan/components/LoanPaymentWidgets/LoanFullyPaidWidget.vue';
import LoanOverdueWidget from '@/loan/components/LoanPaymentWidgets/LoanOverdueWidget.vue';
import LoanNotYetPaidWidget from '@/loan/components/LoanPaymentWidgets/LoanNotYetPaidWidget.vue';
import AmountChip from '@/core/components/AmountChip.vue';
import { UtilHelper } from '@/util/helper';
import { LOAN_FREQUENCY_MAP } from '@/loan/constants/loan-frequency-map';
import PayingAhead from '@/loan/pages/LoanSummary/components/FlipCards/PayingAhead.vue';
import Delinquent from '@/loan/pages/LoanSummary/components/FlipCards/Delinquent.vue';
import PayingMinimum from '@/loan/pages/LoanSummary/components/FlipCards/PayingMinimum.vue';
import { LoanTransactionStatusId } from '@/loan/enums/loan-transaction-status-id';
import { Session } from '@/core/types/session';
import moment from 'moment';

const loanNamespace = namespace('loan');
const coreNamespace = namespace('core');
const MAX_RECENT_TRANSACTIONS = 3;

@Component({
	components: {
		LoanSummaryRecentTransactions,
		LoanSummaryProjection,
		LoanSummaryFIIcon,
		LoanSummaryMetaData,
		LoanESignModal,
		SummaryCard,
		StatusBadge,
		LoanFullyPaidWidget,
		LoanOverdueWidget,
		AmountChip,
		PayingAhead,
		Delinquent,
		PayingMinimum,
		LoanNotYetPaidWidget,
	},
})
export default class LoanSummary extends Vue {
	@loanNamespace.Getter('amountAvailableToTakeBack')
	public amountAvailableToTakeBack!: number;

	@loanNamespace.Getter('autopayFrequency')
	public autopayFrequency!: LoanFrequency;

	@loanNamespace.Getter('autopayPaymentAmount')
	public autopayPaymentAmount!: number;

	@loanNamespace.Getter('autopayPaymentFrequencyText')
	public autopayPaymentFrequencyText!: string;

	@loanNamespace.Getter('autopayTotalAmount')
	public autopayTotalAmount!: number;

	@loanNamespace.Getter('balance')
	public balance!: number;

	@loanNamespace.Getter('calculatedAmountDue')
	public calculatedAmountDue!: number;

	@loanNamespace.Getter('calculatedAmountPastDue')
	public calculatedAmountPastDue!: number;

	@loanNamespace.Getter('fiContactInfo')
	public fiContactInfo!: HelpCenterFIContactInfo;

	public eSignModalState: LoanESignModalState = LoanHelper.getEsignModalDefaultState();

	@loanNamespace.Action('getAndSetLoanDetails')
	public getAndSetLoanDetails!: (loanId: string) => void;

	@loanNamespace.Getter('hasOutstandingFees')
	public hasOutstandingFees!: number;

	@loanNamespace.Getter('interestRate')
	public interestRate!: number;

	@loanNamespace.Getter('isAutopayOn')
	public isAutopayOn!: boolean;

	public isDescriptionChangeLoading = false;

	@loanNamespace.Getter('isMaxDaysPastDue')
	public isMaxDaysPastDue!: boolean;

	@loanNamespace.Getter('isPastDue')
	public isPastDue!: boolean;

	@loanNamespace.Getter('isTakeBackActive')
	public isTakeBackActive!: boolean;

	@loanNamespace.Getter('isTakeBackEnabled')
	public isTakeBackEnabled!: boolean;

	@loanNamespace.State('loan')
	public loan!: Loan;

	@loanNamespace.State('loanDetails')
	public loanDetails!: LoanDetails;

	@loanNamespace.Getter('loanId')
	public loanId!: string;

	@loanNamespace.Getter('outstandingFees')
	public outstandingFees!: number;

	@loanNamespace.Getter('paymentStatus')
	public paymentStatus!: LoanPaymentStatus;

	public paymentStatuses = LoanPaymentStatus;

	@loanNamespace.State('projection')
	public projection!: LoanProjection;

	public projectionChartContext: LoanProjectionChartContext | null = null;

	@coreNamespace.Getter('user')
	public sessionUser!: SessionUser;

	@coreNamespace.Mutation('updateSession')
	public updateSession!: (session: Partial<Session>) => void;

	@loanNamespace.Getter('startBalance')
	public startBalance!: number;

	@loanNamespace.Getter('status')
	public status!: LoanStatus;

	@loanNamespace.State('transactions')
	private transactions!: LoanTransactionWithDetails[];

	public formatDollar = UtilHelper.formatDollar;

	public formatDate = (date: string) => moment(date).format('MM/DD/YYYY');

	get isClosed(): boolean {
		return this.status === LoanStatus.CLOSED;
	}

	get isPaidOff(): boolean {
		return this.status === LoanStatus.PAID;
	}

	get isPayingAhead(): boolean {
		if (
			this.projection?.educationalInfo?.termShortenedBy
			&& Number(this.projection?.educationalInfo?.termShortenedBy) > 0
		) {
			return true;
		}
		return false;
	}

	get pendingTransactions(): LoanTransactionWithDetails[] {
		if (this.transactions && this.transactions.length) {
			const pendingTransactions = this.transactions.filter((t) => t.status === LoanTransactionStatusId.PENDING);
			if (pendingTransactions.length) {
				return pendingTransactions;
			}
		}

		return [];
	}

	get recentTransactions(): LoanTransactionWithDetails[] {
		return this.transactions.slice(0, MAX_RECENT_TRANSACTIONS);
	}

	get autopayNextPaymentDate(): string {
		return UtilHelper.formatISOStringForDisplay(this.loanDetails.recurringPayment.nextRecurringPaymentDate as string);
	}

	get autopayFrequencyForDisplay(): string {
		return LOAN_FREQUENCY_MAP.get(this.autopayFrequency) as string;
	}

	get remainingDueDate(): string {
		return UtilHelper.formatISOStringForDisplay(this.loanDetails.general.paymentDueDate);
	}

	mounted() {
		this.projectionChartContext = {
			projection: this.projection,
			isOriginal: true,
		};

		this.eSignModalState = LoanHelper.getEsignModalDefaultState({
			isShown: this.sessionUser.hasSeenLoanIntro === 0,
		});
	}

	public async onDescriptionChange(description: string): Promise<void> {
		this.isDescriptionChangeLoading = true;

		const request: LoanUpdateLoanRequest = {
			description,
		};

		try {
			await LoanAPIService.updateLoan(this.loanId, request);
			await this.getAndSetLoanDetails(this.loanId);
		} catch (error) {
			console.error(error);
		} finally {
			this.isDescriptionChangeLoading = false;
		}
	}

	public async onESignModalComplete(): Promise<void> {
		this.eSignModalState = {
			...this.eSignModalState,
			isLoading: true,
			wasUpdateSuccessful: false,
			wasUpdateTried: false,
		};

		try {
			await CoreAPIService.updateSessionMeta({
				has_seen_loan_intro: true,
			});

			this.updateSession({
				user: {
					hasSeenLoanIntro: 1,
				} as SessionUser,
			});

			this.eSignModalState = {
				isShown: false,
				isLoading: false,
				wasUpdateSuccessful: true,
				wasUpdateTried: true,
			};
		} catch (error) {
			console.error(error);

			this.eSignModalState = {
				isShown: true,
				isLoading: false,
				wasUpdateSuccessful: false,
				wasUpdateTried: true,
			};
		}
	}
}
